import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import { getOfflineViews } from '~/composables/usePropertyViewTracking';
import { SHORTS_HOME_PATH } from '~/composables/useShortsFilters/buildShortsLocationPath';
import { markShortsOpenIntent } from '~/composables/useVideoQoE';

import { blessSharedVideoElement } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

export function getPropertyDetailsPath(commercialId: string, slug?: string) {
	return slug ? `/imovel/${commercialId}/${slug}` : `/imovel/${commercialId}`;
}

export function useShortsNavigation() {
	const router = useRouter();
	const lastViewedPropertyCookie = useCookie<string>('last-viewed-property');

	// No intent stamp here: this lands on the shorts GRID, which has no player.
	// Stamping would make the later card click report all the time spent
	// browsing the grid as TTFF. The grid stamps its own click instead.
	function navigateToShorts(location?: {
		path: string;
		query?: Record<string, string>;
	}): void {
		blessSharedVideoElement();
		router.push(location ?? { path: SHORTS_HOME_PATH });
	}

	function navigateToVideo(propertyId: string): void {
		markShortsOpenIntent();
		blessSharedVideoElement();
		router.push(`/shorts/${propertyId}`);
	}

	function navigateToPropertyDetails(
		commercialId: string,
		slug?: string
	): void {
		void router.push(getPropertyDetailsPath(commercialId, slug));
	}

	/**
	 * Redireciona para Shorts seguindo a regra dos 3 cenários:
	 * 1. Nunca acessou uma PDP → Home de Shorts (/shorts)
	 * 2. Acessou PDP, nunca consumiu Shorts → Reel do último imóvel visualizado na PDP
	 * 3. Acessou PDP e já consumiu Shorts → Último vídeo assistido no feed
	 */
	/**
	 * Reel que a navegação contextual vai abrir, ou null quando o destino é a
	 * home de shorts. Mesma regra de resolução de navigateToShortsWithContext,
	 * exposta para prefetch por intenção aquecer o destino certo.
	 */
	function resolveShortsContextTarget(): string | null {
		if (!import.meta.client) return null;

		const lastPdpId = lastViewedPropertyCookie.value;
		const lastVideoId = useLastVideoState().get()?.propertyId;
		const lastWatchedShortId = getOfflineViews().sort(
			(a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0)
		)[0]?.propertyId;

		return lastWatchedShortId || lastVideoId || lastPdpId || null;
	}

	function navigateToShortsWithContext(): void {
		if (!import.meta.client) return;

		const target = resolveShortsContextTarget();

		if (!target) return navigateToShorts();
		markShortsOpenIntent();
		blessSharedVideoElement();

		void navigateTo(`/shorts/${target}`);
	}
	return {
		navigateToPropertyDetails,
		navigateToShorts,
		navigateToVideo,
		navigateToShortsWithContext,
		resolveShortsContextTarget
	};
}
